import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Copy,
  ExternalLink,
  FileArchive,
  Globe,
  Loader2,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import webSessionCommand from "@/lib/webSessionCommand.txt?raw";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onDone?: () => void;
}

type Stage = "form" | "done";

const WEB_K_URL = "https://web.telegram.org/k/";

export function AddAccountDialog({ open, onOpenChange, onDone }: Props) {
  const { push } = useToast();
  const [stage, setStage] = useState<Stage>("form");
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("");
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [zipDragOver, setZipDragOver] = useState(false);
  const [copied, setCopied] = useState(false);

  const { data: pool } = useQuery({
    queryKey: ["proxy-pool"],
    queryFn: api.proxyPoolStatus,
    enabled: open,
    refetchInterval: open ? 5_000 : false,
  });

  function resetAll() {
    setStage("form");
    setPhone("");
    setZipFile(null);
    setZipDragOver(false);
    setCopied(false);
  }

  useEffect(() => {
    if (!open) setTimeout(resetAll, 200);
  }, [open]);

  function err(e: unknown) {
    const m = e instanceof ApiError ? e.message : String(e);
    push({ title: "Ошибка", description: m, variant: "destructive" });
  }

  function pickZip(files: FileList | File[] | null) {
    const file = files?.[0];
    if (file) setZipFile(file);
  }

  async function copyCommand() {
    try {
      await navigator.clipboard.writeText(webSessionCommand);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      push({ title: "Команда скопирована", variant: "success" });
    } catch {
      push({ title: "Не удалось скопировать", description: "Выдели текст команды вручную.", variant: "destructive" });
    }
  }

  async function doImport() {
    if (!zipFile) return;
    setLoading(true);
    try {
      const acc = await api.importAccountZip(zipFile);
      setPhone(acc.phone);
      setStage("done");
      push({ title: "Аккаунт импортирован", description: acc.phone, variant: "success" });
      onDone?.();
    } catch (e) { err(e); } finally { setLoading(false); }
  }

  const free = pool?.free_healthy ?? 0;
  // Импорт упирается в наличие здоровых прокси, а не свободных: свободных может
  // не быть ни одного — аккаунт подсядет к наименее загруженному.
  const healthy = pool?.healthy ?? 0;
  const canImport = !!zipFile && healthy > 0 && !loading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Добавить аккаунт</DialogTitle>
          <DialogDescription>
            Аккаунт заводится из сессии Telegram Web. Прокси назначается автоматически из пула; пересадить на конкретный можно на странице «Прокси».
          </DialogDescription>
        </DialogHeader>

        {stage === "form" && (
          <div className="space-y-3">
            <PoolBadge free={free} healthy={healthy} />

            <div className="flex items-start gap-2 rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-3 py-2 text-xs text-[hsl(var(--warning))]">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-medium">Не используй этот вход в Telegram Web после экспорта.</p>
                <p>
                  Панель будет работать с тем же ключом сессии. Если та же веб-вкладка останется активной, Telegram разлогинит и её, и аккаунт на панели. Поэтому входи в&nbsp;Telegram&nbsp;Web в&nbsp;отдельном окне&nbsp;инкогнито Chrome и&nbsp;просто закрой его после экспорта — твой основной вход не&nbsp;пострадает.
                </p>
              </div>
            </div>

            <ol className="space-y-2 text-sm">
              <li className="flex gap-2">
                <StepNum>1</StepNum>
                <div>
                  Открой{" "}
                  <a
                    href={WEB_K_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primary underline decoration-dotted"
                  >
                    web.telegram.org/k/ <ExternalLink className="h-3 w-3" />
                  </a>{" "}
                  в&nbsp;окне инкогнито Chrome (⌘⇧N / Ctrl+Shift+N) и&nbsp;войди в&nbsp;нужный аккаунт.
                </div>
              </li>
              <li className="flex gap-2">
                <StepNum>2</StepNum>
                <div>
                  Открой консоль DevTools: <kbd className="rounded bg-secondary px-1">F12</kbd> (или <kbd className="rounded bg-secondary px-1">⌥⌘J</kbd> на Mac), вкладка <span className="font-medium">Console</span>.
                </div>
              </li>
              <li className="flex gap-2">
                <StepNum>3</StepNum>
                <div>
                  Если консоль не даёт вставить текст — введи <code className="rounded bg-secondary px-1">allow pasting</code> и нажми Enter, потом вставляй команду.
                </div>
              </li>
              <li className="flex gap-2">
                <StepNum>4</StepNum>
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div>Вставь команду, нажми Enter — скачается один <code className="rounded bg-secondary px-1">.zip</code>:</div>
                  <pre className="max-h-24 overflow-y-auto whitespace-pre-wrap break-all rounded border border-border bg-secondary/40 p-2 text-[10px] leading-snug text-muted-foreground">
                    {webSessionCommand}
                  </pre>
                  <Button type="button" variant="outline" size="sm" onClick={copyCommand}>
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? "Скопировано" : "Скопировать команду"}
                  </Button>
                </div>
              </li>
              <li className="flex gap-2">
                <StepNum>5</StepNum>
                <div>Перетащи скачанный <code className="rounded bg-secondary px-1">.zip</code> в&nbsp;поле ниже.</div>
              </li>
            </ol>

            <div>
              <Label>Экспортированный архив (.zip)</Label>
              <label
                className={cn(
                  "mt-1.5 flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed px-3 py-6 text-center text-sm transition-colors",
                  zipDragOver
                    ? "border-primary/60 bg-primary/5 text-primary"
                    : "border-border bg-secondary/40 text-muted-foreground hover:text-foreground"
                )}
                onDragOver={(e) => { e.preventDefault(); setZipDragOver(true); }}
                onDragLeave={() => setZipDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setZipDragOver(false);
                  pickZip(e.dataTransfer.files);
                }}
              >
                <FileArchive className="h-5 w-5 shrink-0" />
                <span className="truncate">
                  {zipFile ? zipFile.name : "Перетащи .zip сюда или выбери"}
                </span>
                <input
                  type="file"
                  accept=".zip,application/zip"
                  className="hidden"
                  onChange={(e) => pickZip(e.target.files)}
                />
              </label>
            </div>

            <Button onClick={doImport} disabled={!canImport} className="w-full mt-2">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {loading ? "Импортирую…" : healthy === 0 ? "Сначала загрузите прокси" : "Импортировать"}
              {!loading && healthy > 0 && <ChevronRight className="h-4 w-4" />}
            </Button>
          </div>
        )}

        {stage === "done" && (
          <div className="flex flex-col items-center gap-3 py-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]">
              <Check className="h-6 w-6" />
            </div>
            <p className="text-sm">Аккаунт <span className="font-medium">{phone}</span> добавлен.</p>
            <p className="text-xs text-muted-foreground">Прокси закреплён за аккаунтом.</p>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Закрыть</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StepNum({ children }: { children: ReactNode }) {
  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[11px] font-semibold text-primary">
      {children}
    </span>
  );
}

function PoolBadge({ free, healthy }: { free: number; healthy: number }) {
  if (healthy === 0) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          В пуле нет здоровых прокси. Загрузите их на странице «Прокси» — без этого аккаунт не запустится.
        </div>
      </div>
    );
  }
  if (free === 0) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
        <Globe className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <div>
          Свободных прокси нет — аккаунт подсядет к наименее загруженному из{" "}
          <span className="font-medium text-foreground">{healthy}</span>. Чем больше аккаунтов на одном IP, тем выше риск бана.
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
      <Globe className="h-3.5 w-3.5" />
      Свободных прокси в пуле: <span className="font-medium text-foreground">{free}</span>
    </div>
  );
}
